// askSmart-ui/src/pages/TOSP/components/BudgetHistoryModal.tsx
import React from "react";
import {
  buildBudgetTimeline,
  BudgetHistory,
  BudgetHistoryStepKey,
  deriveBudgetStatus,
} from "../budgetHistory";

const stamp = (iso?: string): string => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

interface BudgetHistoryModalProps {
  isOpen: boolean;
  budgetLabel: string;
  history: BudgetHistory | undefined;
  onToggleStep: (key: BudgetHistoryStepKey, done: boolean) => void;
  onClose: () => void;
}

// Where a budget is in its round trip, in the same shape as the SMRT COP site
// history: a connected run of steps, each going green as it completes.
//
// Only the next step and the last completed one can be clicked, so the
// progression can never develop a hole — completing a step completes
// everything before it, and undoing one clears everything after.
export default function BudgetHistoryModal({
  isOpen,
  budgetLabel,
  history,
  onToggleStep,
  onClose,
}: BudgetHistoryModalProps) {
  if (!isOpen) return null;

  const timeline = buildBudgetTimeline(history);
  const status = deriveBudgetStatus(history);
  const lastDone = timeline.map((row) => row.done).lastIndexOf(true);

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "520px" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>{budgetLabel} — Status History</h2>
        </div>
        <div className="smrtdocs-modal-body">
          <p className="tosp-field-hint" style={{ marginTop: 0, marginBottom: 16 }}>
            Currently <strong>{status}</strong>. Click the next step to advance, or the last
            completed one to undo it.
          </p>

          <div className="tosp-timeline">
            {timeline.map((row, index) => {
              const isNext = index === lastDone + 1;
              const isLastDone = index === lastDone;
              // PUBLISHING IS NOT A TICK, in either direction. It captures the
              // whole frozen snapshot, and unlocking records a reason and the
              // address it came from — neither can happen by clicking a step,
              // so the row is shown and not offered. Both live on the budget's
              // own toolbar.
              const byTick = row.key !== "published";
              const actionable = (isNext || isLastDone) && byTick;
              return (
                <button
                  key={row.key}
                  type="button"
                  className={`tosp-tl-step${row.done ? " tosp-tl-done" : ""}`}
                  disabled={!actionable}
                  title={
                    actionable
                      ? isLastDone
                        ? `Undo "${row.label}"`
                        : `Mark "${row.label}" done`
                      : !byTick
                        ? row.done
                          ? "Use Unlock Budget on the budget screen"
                          : "Use Publish Budget on the budget screen"
                        : row.done
                          ? "Undo the later steps first"
                          : "Complete the earlier steps first"
                  }
                  onClick={() => onToggleStep(row.key, !row.done)}
                >
                  <span className="tosp-tl-dot" />
                  {/* Just what happened and who did it — the status each step
                      leads to is the budget's state, shown on the list and on
                      the action button, not repeated against every row. */}
                  <span className="tosp-tl-body">
                    <span className="tosp-tl-name">{row.label}</span>
                    <span className="tosp-tl-meta">
                      {row.done
                        ? `${row.byName ? `${row.byName} · ` : ""}${stamp(row.at)}`
                        : "Pending"}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" className="btn-cancel" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
